import { client } from '@/client';
import {
  AccountDomain,
  AccountDomainsResponse,
  AddDomainRequest,
  SendVerificationEmailRequest,
} from '@/client/accounts';
import { MessageResponseModel } from '@/client/models';
import { GenericForbiddenApiError } from '@/hooks/query/types';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

const queryKey = 'account-domains';

export const useAccountDomains = (
  accountId?: string,
  params?: UseQueryOptions,
): UseQueryResult<AccountDomainsResponse> & {
  domains: AccountDomain[] | undefined;
  adminUserCounts: Record<string, number> | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params || {};

  const { data, ...rest } = useQuery(
    [queryKey, accountId],
    () => client.accountDomains.getAccountDomains(accountId as string),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled: !!accountId,
      ...(restOptions as any),
    },
  );

  return {
    domains: data?.result,
    adminUserCounts: data?.adminUserCounts,
    ...(rest as UseQueryResult<AccountDomainsResponse>),
  };
};

export const useAddDomain = (
  accountId: string,
): {
  addDomain: (payload: AddDomainRequest) => Promise<AccountDomain>;
  isLoading: boolean;
} => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    AccountDomain,
    GenericForbiddenApiError,
    AddDomainRequest
  >(
    'addAccountDomain',
    (payload: AddDomainRequest) =>
      client.accountDomains.addDomain(accountId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey, accountId]);
      },
    },
  );

  return { addDomain: mutateAsync, ...(rest as any) };
};

export const useSendVerificationEmail = (
  accountId: string,
): {
  sendVerification: (
    domainId: string,
    payload: SendVerificationEmailRequest,
  ) => Promise<unknown>;
  isLoading: boolean;
} => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    unknown,
    GenericForbiddenApiError,
    { domainId: string; payload: SendVerificationEmailRequest }
  >(
    'sendDomainVerification',
    ({ domainId, payload }) =>
      client.accountDomains.sendVerificationEmail(accountId, domainId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey, accountId]);
      },
    },
  );

  return {
    sendVerification: (domainId, payload) => mutateAsync({ domainId, payload }),
    ...(rest as any),
  };
};

export const useConfirmDomainVerification = (): {
  confirmVerification: (token: string) => Promise<MessageResponseModel>;
  isLoading: boolean;
} => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    string
  >(
    'confirmDomainVerification',
    (token: string) => client.accountDomains.confirmDomainVerification(token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey]);
      },
    },
  );

  return {
    confirmVerification: mutateAsync,
    ...(rest as any),
  };
};

export const useRemoveDomain = (
  accountId: string,
): {
  removeDomain: (domainId: string) => Promise<unknown>;
  isLoading: boolean;
} => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    unknown,
    GenericForbiddenApiError,
    string
  >(
    'removeAccountDomain',
    (domainId: string) =>
      client.accountDomains.removeDomain(accountId, domainId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey, accountId]);
      },
    },
  );

  return { removeDomain: mutateAsync, ...(rest as any) };
};

export const useForceVerifyDomain = (
  accountId: string,
): {
  forceVerifyDomain: (domainId: string) => Promise<AccountDomain>;
  isLoading: boolean;
} => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    AccountDomain,
    GenericForbiddenApiError,
    string
  >(
    'forceVerifyAccountDomain',
    (domainId: string) =>
      client.accountDomains.forceVerifyDomain(accountId, domainId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey, accountId]);
      },
    },
  );

  return { forceVerifyDomain: mutateAsync, ...(rest as any) };
};
